/**
 * @file PickingErrorBoundary.js
 * @description Self-healing error boundary for the picking surface.
 *
 * Deliberately NOT the app-root ErrorBoundary (components/ErrorBoundary.js):
 * that one renders a Polaris Page with an expandable component stack and a
 * "Try Again" button that only resets React state. On a chunk 404 resetting
 * state re-runs the same failing import, and a picker on a tablet has no use
 * for a component stack. This boundary instead names the failure in one line
 * and reloads itself (see utils/pickingRecovery.js for the backoff), which is
 * the only thing that actually fixes the common case.
 *
 * It sits OUTSIDE the Suspense wrapper in App.js so that a lazy import which
 * rejects — the mid-deploy ChunkLoadError — is caught here rather than taking
 * the entire staff app down to the generic error page.
 *
 * Static import on purpose: it has to be on hand precisely when a chunk
 * cannot be fetched.
 */

import React from 'react';
import {
  isChunkLoadError,
  recordCrashAndGetDelay,
  shouldAutoReload
} from '../utils/pickingRecovery';

const GROUND = {
  position: 'fixed',
  inset: 0,
  zIndex: 1100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  background: 'var(--app-bg, #f6f6f7)',
  color: 'var(--app-ink, #202223)'
};

const CARD = {
  maxWidth: 420,
  width: '100%',
  textAlign: 'center',
  padding: '28px 24px',
  borderRadius: 16,
  background: 'var(--app-surface, #ffffff)',
  boxShadow: '0 2px 12px rgba(0,0,0,0.12)'
};

const BUTTON = {
  marginTop: 20,
  width: '100%',
  minHeight: 56,
  fontSize: 18,
  fontWeight: 600,
  borderRadius: 12,
  border: 'none',
  color: '#ffffff',
  background: '#008060',
  cursor: 'pointer'
};

export default class PickingErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, attempt: 0, reloading: false, secondsLeft: 0 };
    this.timer = null;
    this.tick = null;
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[picking] crashed', error, info?.componentStack);
    const { delayMs, attempt } = recordCrashAndGetDelay();
    const reloading = shouldAutoReload({ error, attempt });
    this.setState({ attempt, reloading, secondsLeft: Math.round(delayMs / 1000) });
    if (!reloading) return;
    this.timer = setTimeout(() => this.reload(), delayMs);
    this.tick = setInterval(() => {
      this.setState((s) => ({ secondsLeft: Math.max(0, s.secondsLeft - 1) }));
    }, 1000);
  }

  componentWillUnmount() {
    clearTimeout(this.timer);
    clearInterval(this.tick);
  }

  reload = () => {
    clearTimeout(this.timer);
    clearInterval(this.tick);
    window.location.reload();
  };

  render() {
    const { error, attempt, reloading, secondsLeft } = this.state;
    if (!error) return this.props.children;
    const chunk = isChunkLoadError(error);
    return (
      <div style={GROUND} role="alert">
        <div style={CARD}>
          <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
            {reloading ? 'Reloading picking…' : 'Picking hit an error'}
          </div>
          <p style={{ margin: 0, fontSize: 16, lineHeight: 1.4 }}>
            {chunk
              ? 'A new version of the app was deploying while this loaded.'
              : 'Something went wrong on this screen.'}
            {reloading
              ? ` Reloading in ${secondsLeft}s${attempt > 1 ? ` (attempt ${attempt})` : ''}.`
              : ' Your batch is saved on the server — reloading will bring it back.'}
          </p>
          <button type="button" style={BUTTON} onClick={this.reload}>
            Reload now
          </button>
          <p style={{ marginTop: 14, marginBottom: 0, fontSize: 12, opacity: 0.6 }}>
            {String(error?.message || error).slice(0, 160)}
          </p>
        </div>
      </div>
    );
  }
}
